import React, { useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import { createEvent, EventAttributes } from 'ics';
import { CalendarPlus02 } from '@untitled-ui/icons-react';
import { Button } from '@myhealth-raccoon/components';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import classNames from 'classnames';

import GoogleCalendarIcon from 'src/assets/google-calendar.svg?react';
import AppleIcon from 'src/assets/apple.svg?react';
import OutlookIcon from 'src/assets/outlook.svg?react';

type Props = {
    startDate: Moment,
    endDate?: Moment,
    title: string,
    description?: string,
    address: string,
    geo?: { lat: number, lng: number },

    calendars?: ("google" | "apple" | "outlook" | "ics")[],
    buttonClassName?: string,
}

const filename = "Myhealth_Appointment.ics";

export function CalendarButtons({ startDate, endDate, address, title, description, geo,
    calendars = ["google", "apple", "outlook", "ics"]
}: Props) {
    const [icalUrl, setIcalUrl] = useState("");

    const googleCalendarUrl = useMemo(() => {
        const queryString = new URLSearchParams({
            action: 'TEMPLATE',
            sprop: `website:${import.meta.env.VITE_MYHEALTH_WEBSITE}`,
            dates: `${startDate.format("YYYYMMDDTHHmmSSZ")}${endDate && `/${endDate.format("YYYYMMDDTHHmmSSZ")}`}`,
            location: address,
            text: title,
            details: description ?? "",
        });
        return `https://calendar.google.com/calendar/render?${queryString}`
    }, [address, description, endDate, startDate, title]);

    const outlookCalendarUrl = useMemo(() => {
        const queryString = new URLSearchParams({
            path: '/calendar/action/compose',
            rru: 'addevent',
            startdt: startDate.format("YYYY-MM-DDTHH:mm:SSZ"),
            enddt: endDate ? endDate.format("YYYY-MM-DDTHH:mm:SSZ") : "",
            subject: title,
            body: description ?? "",
            location: address,
        });

        return `https://outlook.office.com/calendar/deeplink/compose?${queryString}`
    }, [address, description, endDate, startDate, title]);

    useEffect(() => {
        (async () => {
            const file = await getIcsFile({
                title,
                description,
                start: [
                    startDate.year(),
                    startDate.month() + 1, // 0 indexed
                    startDate.date(),
                    startDate.hour(),
                    startDate.minute()
                ],
                duration: endDate ? { minutes: moment.duration(endDate.diff(startDate)).asMinutes() } : { minutes: 30 },
                location: address,
                geo: geo && { lat: geo.lat, lon: geo.lng },
            }, filename);
            const url = URL.createObjectURL(file);
            setIcalUrl(url);
        })();
    }, [address, description, endDate, geo, startDate, title]);

    useEffect(() => {
        return () => URL.revokeObjectURL(icalUrl);
    }, [icalUrl])

    return <Popover>
        <PopoverButton className="w-full" color='gray' as={Button}>
            Add to Calendar
        </PopoverButton>
        <PopoverPanel
            transition
            className={classNames(
                "origin-top transition duration-200 ease-out data-[closed]:-translate-y-1 data-[closed]:opacity-0",
                "w-[var(--button-width)] flex flex-col gap-2 p-2 rounded-lg bg-white border shadow"
            )}
            anchor="bottom" as="div">
            {calendars.map(calendar => {
                switch (calendar) {
                    case "google":
                        return <Button key={calendar} color='gray' className='w-full text-center' target='_blank' to={googleCalendarUrl}>
                            <GoogleCalendarIcon className='w-4 h-4 mr-1.5 inline-block' />
                            Google
                        </Button>
                    case "apple":
                        return <Button key={calendar} color='gray' className='w-full text-center' href={icalUrl} download={filename} target='_blank'>
                            <AppleIcon className='w-4 h-4 mr-1.5 inline-block' />
                            Apple
                        </Button>
                    case "outlook":
                        return <Button key={calendar} color='gray' className='w-full text-center' target='_blank' to={outlookCalendarUrl}>
                            <OutlookIcon className='w-4 h-4 mr-1.5 inline-block' />
                            Outlook
                        </Button>
                    case "ics":
                        return <Button key={calendar} color='gray' href={icalUrl} download={filename} className='w-full text-center'>
                            <CalendarPlus02 className='w-4 h-4 mr-1.5 inline-block' />
                            iCal File
                        </Button>
                }
            })}
        </PopoverPanel>
    </Popover>
}

async function getIcsFile(event: EventAttributes, filename: string): Promise<File> {
    return await new Promise((resolve, reject) => {
        createEvent(event, (error, value) => {
            if (error) {
                reject(error)
            }

            resolve(new File([value], filename, { type: 'text/calendar' }))
        })
    });
}